/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { MrpcMessageType, WebSocketError, WebSocketErrorCode } from "./types";
import type { IWebSocketMessage } from "./types";

/**
 * MRPCv2 message enveloper — handles framing and parsing of the MRPCv2 wire protocol.
 *
 * **REQ-SER-01:** Implements `envelope(message)` and `unenvelope(raw)`.
 * **REQ-SER-02:** Conforms to MRPCv2 message framing protocol.
 *
 * Frame format:
 * ```
 * MRPC/2 <headerLengthInBytes> <bodyLengthInBytes>\r\n
 * <Header-Key>: <Header-Value>\r\n
 * ...
 * \r\n\r\n
 * <JSON Body>
 * ```
 */
export class WebSocketMrpcv2Enveloper {
  private static readonly TAG: string = "WebSocketMrpcv2Enveloper";

  private static readonly PROTOCOL_PREFIX: string = "MRPC/2";
  private static readonly HEADER_BODY_SEPARATOR: string = "\r\n\r\n";
  private static readonly LINE_SEPARATOR: string = "\r\n";
  private static readonly HEADER_KV_SEPARATOR: string = ": ";

  /**
   * Falls back to `fallback` when `value` is null or undefined. Stands in for
   * the banned `??` operator (see docs/CHROMIUM-52-COMPATIBILITY.md) and keeps
   * the coalescing branches out of unenvelope()'s complexity budget. The
   * fallback is eagerly evaluated, so pass constants only.
   */
  private static coalesce<T>(value: T | null | undefined, fallback: T): T {
    return value === undefined || value === null ? fallback : value;
  }

  /**
   * Log warnings for missing optional headers that the reference
   * implementation logs but does not reject.
   */
  private static logMissingOptionalHeaders(
    originRequestId: string | undefined,
    bodyId: string | undefined,
    contentType: string | undefined
  ): void {
    if (!originRequestId) {
      Log.e(
        WebSocketMrpcv2Enveloper.TAG,
        "unenvelope(): Origin-RequestId header is missing or empty"
      );
    }
    if (!bodyId) {
      Log.e(
        WebSocketMrpcv2Enveloper.TAG,
        "unenvelope(): BodyId header is missing or empty"
      );
    }
    if (contentType && contentType !== "application/json") {
      Log.e(
        WebSocketMrpcv2Enveloper.TAG,
        `unenvelope(): Unexpected Content-Type: "${contentType}", expected "application/json"`
      );
    }
  }

  /**
   * Unenvelope a raw MRPCv2 frame into a structured WebSocketMessage.
   *
   * @param rawFrame - The raw text frame received from the WebSocket
   * @returns A structured IWebSocketMessage
   * @throws WebSocketError on malformed frames
   */
  public static unenvelope(rawFrame: string): IWebSocketMessage {
    // Step 2.1 — Validate protocol prefix
    if (!rawFrame.startsWith(WebSocketMrpcv2Enveloper.PROTOCOL_PREFIX)) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        "Invalid MRPCv2 frame: does not start with MRPC/2"
      );
    }

    // Step 2.2 — Split into header block and body
    const separatorIndex: number = rawFrame.indexOf(
      WebSocketMrpcv2Enveloper.HEADER_BODY_SEPARATOR
    );
    if (separatorIndex === -1) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        "Invalid MRPCv2 frame: missing header/body separator"
      );
    }

    const headerBlock: string = rawFrame.substring(0, separatorIndex);
    const body: string = rawFrame.substring(
      separatorIndex + WebSocketMrpcv2Enveloper.HEADER_BODY_SEPARATOR.length
    );

    // Step 2.3 — Parse the first line (protocol line)
    const headerLines: string[] = headerBlock.split(
      WebSocketMrpcv2Enveloper.LINE_SEPARATOR
    );
    const firstLine: string = headerLines[0];
    const protocolParts: string[] = firstLine.split(" ");

    if (protocolParts.length < 3) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        `Invalid MRPCv2 protocol line: "${firstLine}"`
      );
    }

    const headerLength: number = parseInt(protocolParts[1], 10);
    const bodyLength: number = parseInt(protocolParts[2], 10);

    if (!Number.isFinite(headerLength) || headerLength <= 0) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        `Invalid header length: ${protocolParts[1]}`
      );
    }

    if (!Number.isFinite(bodyLength) || bodyLength < 0) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        `Invalid body length: ${protocolParts[2]}`
      );
    }

    // Step 2.4 — Parse header key-value pairs
    const headers: Record<string, string> = {};
    for (let i: number = 1; i < headerLines.length; i++) {
      const line: string = headerLines[i];
      if (line.length === 0) {
        continue;
      }
      const colonIndex: number = line.indexOf(
        WebSocketMrpcv2Enveloper.HEADER_KV_SEPARATOR
      );
      if (colonIndex === -1) {
        continue;
      }
      const key: string = line.substring(0, colonIndex);
      const value: string = line.substring(
        colonIndex + WebSocketMrpcv2Enveloper.HEADER_KV_SEPARATOR.length
      );
      headers[key] = value;
    }

    // Step 2.5 — Validate mandatory headers
    const messageType: string | undefined = headers["Type"];
    const requestType: string | undefined = headers["RequestType"];
    const originRequestId: string | undefined = headers["Origin-RequestId"];
    const responseCount: string | undefined = headers["ResponseCount"];
    const rpcId: string | undefined = headers["RpcId"];
    const bodyId: string | undefined = headers["BodyId"];
    const contentType: string | undefined = headers["Content-Type"];

    // Type must be "request" or "response" for valid messages
    if (
      messageType !== MrpcMessageType.REQUEST &&
      messageType !== MrpcMessageType.RESPONSE
    ) {
      const reportedType: string = WebSocketMrpcv2Enveloper.coalesce(
        messageType,
        "(missing)"
      );
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        `Invalid MRPCv2 Type header: "${reportedType}" — expected "request" or "response"`
      );
    }

    // Body-length must be > 0 for request messages
    if (messageType === MrpcMessageType.REQUEST && bodyLength <= 0) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        `Invalid body length for request message: ${protocolParts[2]}`
      );
    }

    // Log warnings for missing optional headers (reference impl logs, does not reject)
    WebSocketMrpcv2Enveloper.logMissingOptionalHeaders(
      originRequestId,
      bodyId,
      contentType
    );

    // ResponseCount must be "single" or "none" — else FeatureNotEnabled
    if (
      responseCount !== undefined &&
      responseCount !== "single" &&
      responseCount !== "none"
    ) {
      throw new WebSocketError(
        WebSocketErrorCode.FEATURE_NOT_ENABLED,
        `Unsupported ResponseCount: "${responseCount}" — must be "single" or "none"`
      );
    }

    // RequestType must not be empty — else FeatureNotEnabled
    if (
      messageType === MrpcMessageType.REQUEST &&
      (!requestType || requestType.trim().length === 0)
    ) {
      throw new WebSocketError(
        WebSocketErrorCode.FEATURE_NOT_ENABLED,
        "RequestType header is missing or empty"
      );
    }

    // Step 2.6 — Construct structured message
    const message: IWebSocketMessage = {
      type:
        messageType === MrpcMessageType.RESPONSE
          ? MrpcMessageType.RESPONSE
          : MrpcMessageType.REQUEST,
      operationName: WebSocketMrpcv2Enveloper.coalesce<string | null>(
        requestType,
        null
      ),
      id: rpcId || "",
      originRequestId: originRequestId || "",
      headers: headers,
      body: body,
      isResponseExpected: responseCount === "single"
    };

    const opForLog: string = WebSocketMrpcv2Enveloper.coalesce(
      message.operationName,
      "null"
    );
    Log.d(
      WebSocketMrpcv2Enveloper.TAG,
      `unenvelope(): type=${message.type}, op=${opForLog}, ` +
        `rpcId=${message.id}, headerLen=${headerLength}, bodyLen=${bodyLength}`
    );

    return message;
  }

  /**
   * Envelope a structured WebSocketMessage into a raw MRPCv2 frame.
   *
   * Used for outbound response messages.
   *
   * @param message - The structured message to envelope
   * @returns A raw MRPCv2 frame string ready for transmission
   * @throws WebSocketError if the message is invalid
   */
  public static envelope(message: IWebSocketMessage): string {
    // Step 3.4 — Validate before building
    if (message.type !== MrpcMessageType.RESPONSE) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        `Cannot envelope non-response message (type: ${message.type})`
      );
    }

    if (!message.body || message.body.trim().length === 0) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        "Cannot envelope: body is empty"
      );
    }

    const trimmedBody: string = message.body.trim();
    if (!trimmedBody.startsWith("{") || !trimmedBody.endsWith("}")) {
      throw new WebSocketError(
        WebSocketErrorCode.ENVELOPE_ERROR,
        "Cannot envelope: body is not valid JSON object"
      );
    }

    // Step 3.1 — Build response headers
    const responseHeaders: string[] = [];
    responseHeaders.push(`Type: ${MrpcMessageType.RESPONSE}`);
    if (message.id) {
      responseHeaders.push(`RpcId: ${message.id}`);
    }
    if (message.originRequestId) {
      responseHeaders.push(`Origin-RequestId: ${message.originRequestId}`);
    }
    responseHeaders.push("IsFinal: true");
    responseHeaders.push("Content-Type: application/json");

    // Step 3.2 — Compute byte lengths
    const headersString: string =
      responseHeaders.join(WebSocketMrpcv2Enveloper.LINE_SEPARATOR) +
      WebSocketMrpcv2Enveloper.HEADER_BODY_SEPARATOR;

    const encoder: TextEncoder = new TextEncoder();
    const headerLength: number = encoder.encode(headersString).byteLength;
    const bodyLength: number = encoder.encode(message.body).byteLength;

    // Step 3.3 — Assemble the frame
    const frame: string =
      `${WebSocketMrpcv2Enveloper.PROTOCOL_PREFIX} ${headerLength} ${bodyLength}` +
      WebSocketMrpcv2Enveloper.LINE_SEPARATOR +
      headersString +
      message.body;

    Log.d(
      WebSocketMrpcv2Enveloper.TAG,
      `envelope(): rpcId=${message.id}, headerLen=${headerLength}, bodyLen=${bodyLength}`
    );

    return frame;
  }
}
